import { Select } from './Select';
import { useRef, KeyboardEvent, RefObject, useMemo, useCallback } from 'react';
import type {
  GroupBase,
  SelectComponentsConfig,
  SelectInstance,
} from 'react-select';
import { components, type MenuProps } from 'react-select';
import styled from 'styled-components';
import { TextArea, TextAreaProps } from '@ardoq/forms';
import { useOnClickOutside } from '@ardoq/hooks';
import { SelectOption } from '../types';
import { colors, zIndex } from '@ardoq/design-tokens';

export type TextAreaWithSuggestionsProps = TextAreaProps & {
  suggestions: SelectOption<string>[];
  suggestionsIsOpen: boolean;
  onSelectSuggestion: (value: string | null) => void;
  onClickOutside: VoidFunction;
};

/**
 * Custom component is useful for two purposes
 * 1. We can detect click outside events and propagate that up to the parent caller
 * 2. We can stop propagation of click events. This helps when the menu is rendered in a popover and clicking on the menu causes the popover itself to close
 */
const CustomMenu = (
  props: MenuProps<SelectOption<unknown>> & {
    onClickOutside: VoidFunction;
    hasKeyboardFocus: boolean;
  }
) => {
  useOnClickOutside(
    props.innerRef as RefObject<HTMLElement>,
    props.onClickOutside
  );

  return (
    <StyledMenu
      ref={props.innerRef}
      onClick={e => e.stopPropagation()}
      className={props.hasKeyboardFocus ? 'is-focused' : ''}
    >
      <components.Menu {...props}>{props.children}</components.Menu>
    </StyledMenu>
  );
};

export const TextAreaWithSuggestions = ({
  suggestionsIsOpen,
  suggestions,
  onSelectSuggestion,
  onClickOutside,
  ...textareaProps
}: TextAreaWithSuggestionsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const selectRef = useRef<SelectInstance<SelectOption<string>> | null>(null);
  const menuIsFocused = useRef(false);

  const handleTextAreaKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    textareaProps.onKeyDown?.(e);
    if (e.key === 'ArrowDown' && suggestionsIsOpen) {
      menuIsFocused.current = true;
      selectRef.current?.focus();
      selectRef.current?.focusOption('first');
    }
  };

  const handleSelectKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') {
      const focusedOptionId = selectRef.current?.focusedOptionRef?.id;

      if (!focusedOptionId) return;

      /**
       * If the currently focused option is the first option on the list jump focus back to the textarea
       * React select doesn't seem to expose a more elegant way to get the actual option index,
       * which is why I went with string comparison
       * */
      if (focusedOptionId.endsWith('option-0')) {
        e.preventDefault();
        textAreaRef.current?.focus();
        menuIsFocused.current = false;
      }
    }
  };

  const handleSelectSuggestion = (value: string) => {
    menuIsFocused.current = false;
    onSelectSuggestion(value);

    // Wait one tick then focus on textarea
    setTimeout(() => textAreaRef.current?.focus());
  };

  const handleClickOutside = useCallback(() => {
    onClickOutside();
    menuIsFocused.current = false;
  }, [onClickOutside]);

  const components = useMemo<
    SelectComponentsConfig<
      SelectOption<unknown>,
      false,
      GroupBase<SelectOption<unknown>>
    >
  >(
    () => ({
      Menu: props => (
        <CustomMenu
          {...(props as MenuProps<SelectOption<unknown>>)}
          onClickOutside={handleClickOutside}
          hasKeyboardFocus={menuIsFocused.current}
        />
      ),
    }),
    [handleClickOutside]
  );

  return (
    <AutocompleteWrapper ref={wrapperRef}>
      <CustomSelect
        ref={selectRef}
        options={suggestions}
        menuIsOpen={suggestionsIsOpen}
        onValueChange={value => handleSelectSuggestion(value as string)}
        onKeyDown={handleSelectKeyDown}
        controlShouldRenderValue={false}
        menuPlacement="bottom"
        menuPortalTarget={wrapperRef.current}
        menuPosition="fixed"
        autoFocus={false}
        components={components}
        styles={{
          // Helps ensure that the select is positioned correctly relative to the textarea https://github.com/JedWatson/react-select/issues/4680#issuecomment-883375414
          menuPortal: ({ left: _left, top: _top, ...rest }) => ({
            ...rest,
            zIndex: zIndex.DROPDOWN,
          }),
        }}
      />
      <TextArea
        {...textareaProps}
        ref={textAreaRef}
        onKeyDown={handleTextAreaKeyDown}
      />
    </AutocompleteWrapper>
  );
};

const AutocompleteWrapper = styled.div`
  position: relative;

  textarea {
    position: relative;
    z-index: 2;
  }
`;

const CustomSelect = styled(Select)`
  position: absolute !important; /* react-select's default styling overwrites without !important */
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const StyledMenu = styled.div`
  &:not(.is-focused) [role='option']:not(:hover) {
    background-color: ${colors.surfaceDefault};
  }
`;
